import { useState } from "react";
import { Calculator, Percent, X, Zap } from "lucide-react";

const fmt2 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt4 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

/**
 * POS "Hızlı Araçlar" Çekmecesi — controlled component: parent açar/kapatır.
 * FAB kaldırıldı, artık POS barkod bar'ının yanındaki şimşek ikonu bunu tetikler.
 * Data-testids: pos-tools-drawer, pos-tools-tab-{calc|comm}, calc-*, comm-*
 */
export default function POSToolsDrawer({ open, onClose }) {
  const [tab, setTab] = useState("calc");

  // ------ Calculator state ------
  const [expr, setExpr] = useState("");
  const [result, setResult] = useState("");
  const pushKey = (k) => setExpr((v) => v + k);
  const clearCalc = () => { setExpr(""); setResult(""); };
  const backspace = () => setExpr((v) => v.slice(0, -1));
  const evalCalc = () => {
    try {
      // Güvenli mini eval — sadece rakam ve + - * / . % ( )
      const clean = expr.replace(/,/g, ".").replace(/[^0-9+\-*/.()% ]/g, "");
      // "%" → "/100"
      const safe = clean.replace(/(\d+(\.\d+)?)%/g, "($1/100)");
      // eslint-disable-next-line no-new-func
      const r = Function('"use strict";return (' + safe + ")")();
      setResult(String(Number(r).toLocaleString("tr-TR", { maximumFractionDigits: 4 })));
    } catch { setResult("Hatalı"); }
  };

  // ------ Commission state ------
  const [grPrice, setGrPrice] = useState("");   // gr fiyat
  const [gramaj, setGramaj]   = useState("");   // toplam gramaj
  const [ayarP, setAyarP]     = useState("916"); // milyem (‰)
  const [iscilikP, setIscilikP] = useState("8"); // işçilik yüzdesi
  const [iscilikTL, setIscilikTL] = useState(""); // veya sabit TL
  const [komP, setKomP] = useState("2");         // komisyon %

  const baz = (Number(gramaj) || 0) * (Number(ayarP) / 1000) * (Number(grPrice) || 0);
  const iscilik = iscilikTL !== ""
    ? Number(iscilikTL) || 0
    : baz * ((Number(iscilikP) || 0) / 100);
  const komisyon = baz * ((Number(komP) || 0) / 100);
  const toplam = baz + iscilik + komisyon;

  const CalcBtn = ({ label, onClick, cls = "", "data-testid": testid }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`h-10 sm:h-11 border border-white/12 hover:border-[--gold] hover:text-[--gold] active:scale-95 transition text-sm font-mono ${cls}`}
    >
      {label}
    </button>
  );

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm"
          onClick={onClose}
          data-testid="pos-tools-drawer-backdrop"
        >
          <aside
            className="absolute top-0 right-0 h-full w-full max-w-sm bg-[#0a0a0a] border-l border-[--gold]/40 flex flex-col animate-in slide-in-from-right"
            style={{ borderColor: "rgba(212,175,55,0.4)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="pos-tools-drawer"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Zap size={16} style={{ color: "var(--gold)" }} />
                <div className="font-display text-sm tracking-widest">HIZLI ARAÇLAR</div>
              </div>
              <button
                onClick={onClose}
                aria-label="Kapat"
                className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-red-400 hover:text-red-300 transition"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-2 border-b border-white/5">
              <button
                onClick={() => setTab("calc")}
                data-testid="pos-tools-tab-calc"
                className={`py-3 flex items-center justify-center gap-2 text-xs uppercase tracking-widest transition ${tab === "calc" ? "text-[--gold] border-b-2 border-[--gold]" : "text-neutral-500 hover:text-white"}`}
              >
                <Calculator size={12} /> Hesap
              </button>
              <button
                onClick={() => setTab("comm")}
                data-testid="pos-tools-tab-comm"
                className={`py-3 flex items-center justify-center gap-2 text-xs uppercase tracking-widest transition ${tab === "comm" ? "text-[--gold] border-b-2 border-[--gold]" : "text-neutral-500 hover:text-white"}`}
              >
                <Percent size={12} /> Komisyon
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4">
              {tab === "calc" && (
                <div>
                  <input
                    value={expr}
                    onChange={(e) => setExpr(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 px-3 py-3 text-right text-xl font-mono tabular-nums focus:outline-none focus:border-[--gold]"
                    placeholder="0"
                    data-testid="calc-input"
                  />
                  <div className="text-right font-mono text-2xl mt-2 tabular-nums h-8" style={{ color: "var(--gold)" }} data-testid="calc-result">
                    {result || ""}
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 mt-3">
                    <CalcBtn label="C" onClick={clearCalc} cls="text-red-400" data-testid="calc-clear" />
                    <CalcBtn label="⌫" onClick={backspace} data-testid="calc-back" />
                    <CalcBtn label="%" onClick={() => pushKey("%")} />
                    <CalcBtn label="÷" onClick={() => pushKey("/")} cls="text-[--gold]" />
                    <CalcBtn label="7" onClick={() => pushKey("7")} />
                    <CalcBtn label="8" onClick={() => pushKey("8")} />
                    <CalcBtn label="9" onClick={() => pushKey("9")} />
                    <CalcBtn label="×" onClick={() => pushKey("*")} cls="text-[--gold]" />
                    <CalcBtn label="4" onClick={() => pushKey("4")} />
                    <CalcBtn label="5" onClick={() => pushKey("5")} />
                    <CalcBtn label="6" onClick={() => pushKey("6")} />
                    <CalcBtn label="−" onClick={() => pushKey("-")} cls="text-[--gold]" />
                    <CalcBtn label="1" onClick={() => pushKey("1")} />
                    <CalcBtn label="2" onClick={() => pushKey("2")} />
                    <CalcBtn label="3" onClick={() => pushKey("3")} />
                    <CalcBtn label="+" onClick={() => pushKey("+")} cls="text-[--gold]" />
                    <CalcBtn label="0" onClick={() => pushKey("0")} cls="col-span-2" />
                    <CalcBtn label="." onClick={() => pushKey(".")} />
                    <CalcBtn label="=" onClick={evalCalc} cls="text-black bg-[--gold]" data-testid="calc-eq" />
                  </div>
                </div>
              )}

              {tab === "comm" && (
                <div className="space-y-3" data-testid="comm-panel">
                  <div>
                    <label className="label-caps text-[10px]">Gram Fiyatı (₺/gr)</label>
                    <input value={grPrice} onChange={(e) => setGrPrice(e.target.value)} inputMode="decimal" placeholder="6.700" className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[--gold]" data-testid="comm-gr-price" />
                  </div>
                  <div>
                    <label className="label-caps text-[10px]">Gramaj</label>
                    <input value={gramaj} onChange={(e) => setGramaj(e.target.value)} inputMode="decimal" placeholder="15,42" className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[--gold]" data-testid="comm-gramaj" />
                  </div>
                  <div>
                    <label className="label-caps text-[10px]">Ayar (‰)</label>
                    <select value={ayarP} onChange={(e) => setAyarP(e.target.value)} className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[--gold]" data-testid="comm-ayar">
                      <option value="999">999 (24K)</option>
                      <option value="916">916 (22K)</option>
                      <option value="875">875 (21K)</option>
                      <option value="750">750 (18K)</option>
                      <option value="585">585 (14K)</option>
                      <option value="417">417 (10K)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="label-caps text-[10px]">İşçilik %</label>
                      <input value={iscilikP} onChange={(e) => { setIscilikP(e.target.value); setIscilikTL(""); }} inputMode="decimal" placeholder="8" className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[--gold]" />
                    </div>
                    <div>
                      <label className="label-caps text-[10px]">veya ₺ Sabit</label>
                      <input value={iscilikTL} onChange={(e) => setIscilikTL(e.target.value)} inputMode="decimal" placeholder="0" className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[--gold]" />
                    </div>
                  </div>
                  <div>
                    <label className="label-caps text-[10px]">Komisyon %</label>
                    <input value={komP} onChange={(e) => setKomP(e.target.value)} inputMode="decimal" placeholder="2" className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[--gold]" data-testid="comm-kom" />
                  </div>
                  <div className="border-t border-white/5 pt-3 space-y-1.5 text-xs font-mono">
                    <div className="flex justify-between"><span className="text-neutral-500">Baz Değer</span><span className="tabular-nums">{fmt2(baz)} ₺</span></div>
                    <div className="flex justify-between"><span className="text-neutral-500">İşçilik</span><span className="tabular-nums">{fmt2(iscilik)} ₺</span></div>
                    <div className="flex justify-between"><span className="text-neutral-500">Komisyon</span><span className="tabular-nums">{fmt2(komisyon)} ₺</span></div>
                    <div className="flex justify-between text-lg pt-2 border-t border-white/5">
                      <span>TOPLAM</span>
                      <span className="tabular-nums" style={{ color: "var(--gold)" }} data-testid="comm-total">{fmt2(toplam)} ₺</span>
                    </div>
                    <div className="flex justify-between text-[10px] pt-1">
                      <span className="text-neutral-500">Has Karşılığı</span>
                      <span className="tabular-nums text-neutral-400">{fmt4((Number(gramaj) || 0) * (Number(ayarP) / 1000))} gr</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
